import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  PLATFORM_ID,
  SimpleChanges
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Subscription, timer } from 'rxjs';
import { exhaustMap } from 'rxjs/operators';
import * as QRCode from 'qrcode';

import { PhoneUploadSessionHttpService } from '../phone-upload-session.http-service';
import {
  PHONE_UPLOAD_POLL_MS,
  PhoneUploadContext,
  PhoneUploadFile,
  PhoneUploadSession
} from '../phone-upload.models';

type PanelState = 'starting' | 'waiting' | 'received';

/** Consecutive failed checks before telling the user we lost the connection. */
const OFFLINE_AFTER_FAILURES = 3;
/** How long "Got it!" shows, and how long before a full code is replaced. */
export const NEXT_CODE_AFTER_MS = 4000;
/** Retry delays when a code can't be created; the last one repeats. */
export const CREATE_RETRY_MS = [2000, 5000, 10000, 30000];

/**
 * Shows a QR code that opens the phone upload page, then waits for files.
 *
 * The person never manages codes: the panel creates one as soon as it knows the
 * company, keeps it up while the phone sends more files, replaces it once it is
 * full or expired, and keeps retrying quietly if the server can't be reached.
 *
 * Self-contained so it can sit inside the Uploadcare widget (via the phone upload
 * plugin), inline in the gallery step, or anywhere else on a page. It emits each
 * newly received file once; the host decides what to do with it.
 */
@Component({
  selector: 'phone-upload-panel',
  templateUrl: './phone-upload-panel.component.html',
  styleUrls: ['./phone-upload-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PhoneUploadPanelComponent implements OnChanges, OnDestroy {
  /** Can arrive before the company is known; the code is created once it is. */
  @Input() context: PhoneUploadContext;
  /** Smaller layout for tight spaces such as the gallery step. */
  @Input() compact = false;
  @Output() filesReceived = new EventEmitter<PhoneUploadFile[]>();

  public state: PanelState = 'starting';
  public qrDataUrl: string | null = null;
  public mobileUrl: string | null = null;
  public receivedCount = 0;
  public offline = false;
  /** A file arrived on a code that is still open: "Got it!" shows under the code for a moment. */
  public justReceived = false;

  private sessionId: string | null = null;
  private ownerToken: string | null = null;
  private expiresAt = 0;
  private seen = new Set<string>();
  private failures = 0;
  private createFailures = 0;
  private started = false;
  private createSubscription: Subscription | null = null;
  private pollSubscription: Subscription | null = null;
  private nextCodeSubscription: Subscription | null = null;
  private justReceivedSubscription: Subscription | null = null;
  private readonly isBrowser: boolean;

  constructor(
    private sessions: PhoneUploadSessionHttpService,
    private detector: ChangeDetectorRef,
    @Inject(PLATFORM_ID) platformId: Object
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.context && !this.started && this.isBrowser && this.context?.companyId) {
      this.started = true;
      this.start();
    }
  }

  ngOnDestroy(): void {
    this.stop(true);
  }

  public get statusText(): string {
    if (this.state === 'starting') {
      return this.createFailures >= 2 ? 'Still connecting…' : 'Getting your code…';
    }
    if (this.state === 'received' || this.justReceived) {
      return 'Got it! Your file is being added.';
    }
    if (this.offline) {
      return 'Connection lost. Still trying…';
    }
    return '';
  }

  private start(): void {
    this.setState('starting');
    this.createSubscription = this.sessions.create(this.context).subscribe({
      next: session => {
        this.createFailures = 0;
        this.sessionId = session.id;
        this.ownerToken = session.owner_token;
        this.mobileUrl = session.mobile_url;
        this.expiresAt = Date.parse(session.expires_at) || 0;
        QRCode.toDataURL(session.mobile_url, { margin: 1, width: 240, errorCorrectionLevel: 'M' })
          .then(url => {
            this.qrDataUrl = url;
            this.setState('waiting');
            this.poll();
          })
          .catch(() => this.retryCreate());
      },
      error: () => this.retryCreate()
    });
  }

  /** Swaps in a fresh code without any action from the person. */
  private renew(): void {
    this.stop(true);
    this.start();
  }

  private retryCreate(): void {
    const delay = CREATE_RETRY_MS[Math.min(this.createFailures, CREATE_RETRY_MS.length - 1)];
    this.createFailures++;
    this.setState('starting');
    this.nextCodeSubscription = timer(delay).subscribe(() => this.renew());
  }

  private poll(): void {
    this.pollSubscription = timer(PHONE_UPLOAD_POLL_MS, PHONE_UPLOAD_POLL_MS)
      .pipe(exhaustMap(() => this.sessions.getSession(this.sessionId, this.ownerToken)))
      .subscribe({
        next: session => this.onSession(session),
        error: (error: HttpErrorResponse) => this.onPollError(error)
      });
  }

  private onSession(session: PhoneUploadSession): void {
    this.failures = 0;
    this.offline = false;
    const fresh = (session.files || []).filter(file => !this.seen.has(file.uuid));
    fresh.forEach(file => this.seen.add(file.uuid));
    this.receivedCount = this.seen.size;
    if (fresh.length) {
      this.filesReceived.emit(fresh);
      this.showJustReceived();
    }

    if (session.status === 'complete') {
      this.stop(false);
      this.setState('received');
      this.nextCodeSubscription = timer(NEXT_CODE_AFTER_MS).subscribe(() => this.renew());
    } else if (session.status !== 'open' || this.isExpired()) {
      this.renew();
    } else {
      this.detector.markForCheck();
    }
  }

  private onPollError(error: HttpErrorResponse): void {
    this.pollSubscription = null;
    if (error && (error.status === 404 || error.status === 403 || error.status === 410)) {
      // The code is gone (expired or closed): show a new one.
      this.renew();
      return;
    }
    // Network blips and server errors: keep trying; replace the code once it expires.
    this.failures++;
    this.offline = this.failures >= OFFLINE_AFTER_FAILURES;
    if (this.isExpired()) {
      this.renew();
      return;
    }
    this.detector.markForCheck();
    this.poll();
  }

  private showJustReceived(): void {
    this.justReceived = true;
    this.justReceivedSubscription?.unsubscribe();
    this.justReceivedSubscription = timer(NEXT_CODE_AFTER_MS).subscribe(() => {
      this.justReceived = false;
      this.detector.markForCheck();
    });
  }

  private isExpired(): boolean {
    return !!this.expiresAt && Date.now() > this.expiresAt;
  }

  /** Stops polling; with `cancel`, also closes an open session so the phone can’t keep sending. */
  private stop(cancel: boolean): void {
    this.createSubscription?.unsubscribe();
    this.createSubscription = null;
    this.pollSubscription?.unsubscribe();
    this.pollSubscription = null;
    this.nextCodeSubscription?.unsubscribe();
    this.nextCodeSubscription = null;
    this.justReceivedSubscription?.unsubscribe();
    this.justReceivedSubscription = null;
    this.justReceived = false;
    if (cancel && this.sessionId && this.ownerToken && this.state === 'waiting') {
      this.sessions.cancel(this.sessionId, this.ownerToken).subscribe({ error: () => undefined });
    }
    if (cancel) {
      this.sessionId = null;
      this.ownerToken = null;
      this.qrDataUrl = null;
      this.mobileUrl = null;
    }
    this.failures = 0;
    this.offline = false;
  }

  private setState(state: PanelState): void {
    this.state = state;
    this.detector.markForCheck();
  }
}
